import { Component, OnInit, Input, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { Select, Store } from '@ngxs/store';
import { Observable, timer, Subject } from 'rxjs';
import { EditPaintState } from 'src/app/edit/state/paint/edit.paint.state';
import { filter, debounce, takeUntil } from 'rxjs/operators';
import { SetColorRecipe } from 'src/app/edit/state/paint/edit.paint.actions';
import { ColorService } from 'src/app/shared/color.service';
import { ColorFresnelModel } from 'src/app/edit/models/color-fresnel.model';
import { ScalarFresnelModel } from 'src/app/edit/models/scalar-fresnel.model';
import { PaintDefaults } from 'src/app/edit/models/paint-defaults.model';

@Component({
  selector: 'app-paint-widget',
  templateUrl: './paint-widget.component.html',
  styleUrls: ['./paint-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaintWidgetComponent implements OnInit, OnDestroy {
  @Select(EditPaintState.getColorRecipe)
  colorRecipe$: Observable<ColorRecipeModel>;

  @Input()
  label: string;

  colorPickerHex = '#ffffff';
  colorRecipe: ColorRecipeModel;
  form: FormGroup;

  fresnelColor = false;
  fresnelOpacity = false;

  private ngUnsubscribe = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    private colorService: ColorService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.form = this.formBuilder.group({
      'tintColor': [[...PaintDefaults.tintColor]],
      'saturation': [PaintDefaults.saturation],
      'contrast': [PaintDefaults.contrast],
      'brightness': [PaintDefaults.brightness],
      'opacity': [PaintDefaults.opacity]
    });
  }

  ngOnInit() {
    // Handle change to color recipe
    this.colorRecipe$
      .pipe(
        filter(colorRecipe => {
          return colorRecipe != null &&
            JSON.stringify(colorRecipe) !== JSON.stringify(this.colorRecipe);
        },
        takeUntil(this.ngUnsubscribe)
      )
    ).subscribe((colorRecipe) => {
      this.colorRecipe = colorRecipe;

      this.fresnelColor = colorRecipe.tintColor instanceof ColorFresnelModel;
      this.fresnelOpacity = colorRecipe.opacity instanceof ScalarFresnelModel;

      this.setFormValues(colorRecipe);
      this.changeDetector.markForCheck();
    });

    // Handle change in form
    this.form.valueChanges
      .pipe(
        debounce(() => timer(300)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((values) => {
        this.handleFormChanges(values);
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setFormValues(colorRecipe: ColorRecipeModel) {
    // Tint
    let tintColor = PaintDefaults.tintColor;
    if (!this.fresnelColor && colorRecipe.tintColor !== false) {
      this.colorPickerHex = this.colorService.rgbToHex(colorRecipe.tintColor as [number, number, number], true);
      tintColor = colorRecipe.tintColor as [number, number, number];
    }

    // Opacity
    let opacity = PaintDefaults.opacity;
    if (!this.fresnelOpacity && colorRecipe.opacity !== false) {
      opacity = colorRecipe.opacity as number;
    }

    // Contrast
    let contrast = PaintDefaults.contrast;
    if (colorRecipe.contrast !== false) {
      contrast = colorRecipe.contrast as number;
    }

    // Brightness
    let brightness = PaintDefaults.brightness;
    if (colorRecipe.brightness !== false) {
      brightness = colorRecipe.brightness as number;
    }

    // Saturation
    let saturation = PaintDefaults.saturation;
    if (colorRecipe.saturation !== false) {
      saturation = colorRecipe.saturation as number;
    }

    this.form.setValue({
      tintColor: tintColor,
      saturation: saturation,
      contrast: contrast,
      brightness: brightness,
      opacity: opacity
    });
  }

  handleFormChanges(formValues) {
    const recipe = {...this.colorRecipe};

    recipe.saturation = formValues.saturation; 
    recipe.brightness = formValues.brightness; 
    recipe.contrast = formValues.contrast; 

    if (!this.fresnelOpacity) {
      recipe.opacity = formValues.opacity; 
    }

    if (!this.fresnelColor) {
      recipe.tintColor = (typeof formValues.tintColor === 'string') ? this.colorService.hexToRgb(formValues.tintColor, true) : formValues.tintColor;
    }

    this.store.dispatch(new SetColorRecipe(recipe));
  }
  
  handleColorPickerChange(color) {
    this.colorPickerHex = color;
    
    this.form.patchValue({
      'tintColor': this.colorService.hexToRgb(color, true)
    });
  }
  
}
